// composables
import { ref, watch, computed, type Ref } from 'vue'
// utilities
import { useI18n } from 'vue-i18n'
// router
import { useRoute, useRouter } from 'vue-router'

/**
 * Composable for managing agreement tabs and routing
 * Handles tab state, route synchronization, and tab definitions
 */
export default function useAgreementTabs(agreementID: Ref<string | number>, appointmentsCounter: Ref<number>) {
  const route = useRoute()
  const router = useRouter()
  const { t } = useI18n()

  // state
  const currentTab = ref<string>((route.params.tab as string) || 'overview')

  /**
   * Tab definitions
   */
  const tabs = computed(() => [
    {
      id: 'overview',
      label: 'Overview',
      icon: 'fa-eye',
      to: '/agreement/' + agreementID.value,
      isActive: true
    },
    {
      id: 'activity',
      label: 'Activity',
      icon: 'fa-wave-pulse',
      to: '/agreement/' + agreementID.value + '/activity',
      isActive: true
    },
    {
      id: 'conversations',
      label: t('ui.entities.conversation', 2),
      icon: 'fa-messages',
      to: '/agreement/' + agreementID.value + '/conversations'
    },
    {
      id: 'appointments',
      label: t('ui.entities.appointment.label', 2),
      icon: 'fa-calendar',
      to: '/agreement/' + agreementID.value + '/appointments',
      counter: appointmentsCounter.value
    },
    {
      id: 'vehicle',
      label: t('ui.entities.vehicle.label'),
      icon: 'fa-car',
      to: '/agreement/' + agreementID.value + '/vehicle'
    },
    {
      id: 'switch',
      label: t('ui.entities.switch'),
      icon: 'fa-arrows-repeat',
      to: '/agreement/' + agreementID.value + '/switch',
      isHidden: true
    }
  ])

  /**
   * Watch for route param changes and update current tab
   */
  watch(
    () => route.params.tab,
    (newTab, oldTab) => {
      if (newTab != oldTab) {
        // Handle undefined or empty string as overview tab
        if (!newTab || newTab === '') {
          currentTab.value = 'overview'
        } else {
          currentTab.value = newTab as string
        }
      }
    },
    {
      immediate: true
    }
  )

  /**
   * Watch for current tab changes and update route
   */
  watch(
    () => currentTab.value,
    (newTab, oldTab) => {
      // Only replace route if tab actually changed
      if (newTab === oldTab) {
        return
      }

      // Fetch tab route
      const tab = tabs.value.find(tab => tab.id === newTab)
      if (tab) {
        // Only preserve the id parameter if switching to appointments tab
        // Other tabs (like overview) should not include the id parameter
        const currentId = route.params.id
        const newRoute = newTab === 'appointments' && currentId ? `${tab.to}/${currentId}` : tab.to

        // Only replace if the route is actually different to avoid unnecessary navigation
        if (route.path !== newRoute) {
          router.replace(newRoute)
        }
      }
    }
  )

  return {
    // state
    currentTab,
    // computed
    tabs
  }
}
